package components.auth

import androidx.compose.runtime.*
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import styles.AppStyleSheet
import design.DesignTokens
import kotlinx.coroutines.launch

/**
 * Onboarding Welcome Screen
 * Shown after successful registration to guide users to the main features
 */
@Composable
fun OnboardingWelcome(
    username: String,
    onStart: () -> Unit,
    onSetupTotp: () -> Unit
) {
    var currentStep by remember { mutableStateOf(0) }
    val steps = remember {
        listOf(
            OnboardingStep(
                icon = "📚",
                title = "読書メモを記録",
                description = "読んだ本の感想やメモを簡単に記録できます。あとで振り返える読書記録を作りましょう。"
            ),
            OnboardingStep(
                icon = "📸",
                title = "スクリーンショットを保存",
                description = "本の中の気になる部分や重要な箇所をスクリーンショットとして保存できます。"
            ),
            OnboardingStep(
                icon = "🔒",
                title = "2要素認証で保護",
                description = "TOTPによる2要素認証を設定して、アカウントを安全に保護しましょう。"
            )
        )
    }

    LaunchedEffect(Unit) {
        // Auto-advance through steps
        while (currentStep < steps.size) {
            kotlinx.coroutines.delay(4000)
            if (currentStep < steps.size - 1) {
                currentStep++
            }
        }
    }

    Div({
        classes(AppStyleSheet.loginContainer)
        style {
            property("max-width", "600px")
        }
    }) {
        Div({ classes(AppStyleSheet.loginForm) }) {
            // Progress indicator
            if (currentStep < steps.size) {
                Div({
                    style {
                        property("display", "flex")
                        property("gap", "8px")
                        property("margin-bottom", "32px")
                        property("justify-content", "center")
                    }
                }) {
                    steps.forEachIndexed { index, _ ->
                        Div({
                            style {
                                property("width", if (index == currentStep) "32px" else "12px")
                                property("height", "12px")
                                property("border-radius", "6px")
                                property("background-color",
                                    if (index == currentStep)
                                        DesignTokens.Colors.primary.toString()
                                    else if (index < currentStep)
                                        DesignTokens.Colors.success.toString()
                                    else
                                        "#e0e0e0")
                                property("transition", "all 0.3s ease")
                            }
                        }) {}
                    }
                }
            }

            // Welcome message
            if (currentStep == 0) {
                H2({
                    classes(AppStyleSheet.loginTitle)
                    style {
                        property("margin-bottom", "8px")
                    }
                }) {
                    Text("ようこそ、${username}さん！")
                }

                P({
                    style {
                        property("color", DesignTokens.Colors.textSecondary.toString())
                        property("margin-bottom", "32px")
                        property("text-align", "center")
                    }
                }) {
                    Text("アカウントの作成が完了しました")
                }
            }

            // Step content
            if (currentStep < steps.size) {
                val step = steps[currentStep]

                // Feature illustration
                Div({
                    style {
                        property("font-size", "64px")
                        property("text-align", "center")
                        property("margin-bottom", "24px")
                        property("animation", "fadeInUp 0.5s ease")
                    }
                }) {
                    Text(step.icon)
                }

                H3({
                    style {
                        property("font-size", "24px")
                        property("font-weight", "600")
                        property("margin-bottom", "12px")
                        property("text-align", "center")
                        property("color", DesignTokens.Colors.text.toString())
                    }
                }) {
                    Text(step.title)
                }

                P({
                    style {
                        property("color", DesignTokens.Colors.textSecondary.toString())
                        property("margin-bottom", "32px")
                        property("text-align", "center")
                        property("line-height", "1.6")
                    }
                }) {
                    Text(step.description)
                }

                // Navigation buttons
                Div({
                    classes(AppStyleSheet.loginButtonGroup)
                    style {
                        property("flex-direction", "row")
                        property("gap", "12px")
                    }
                }) {
                    Button({
                        classes(AppStyleSheet.secondaryButton)
                        style {
                            property("flex", "1")
                        }
                        if (currentStep == 0) attr("disabled", "true")
                        onClick {
                            if (currentStep > 0) {
                                currentStep--
                            }
                        }
                    }) {
                        Text("戻る")
                    }

                    Button({
                        classes(AppStyleSheet.primaryButton)
                        style {
                            property("flex", "1")
                        }
                        onClick {
                            if (currentStep < steps.size - 1) {
                                currentStep++
                            }
                        }
                    }) {
                        Text(if (currentStep == steps.size - 1) "次へ" else "次へ")
                    }
                }
            } else {
                // Final screen - call to action
                Div({
                    style {
                        property("text-align", "center")
                        property("animation", "fadeInUp 0.5s ease")
                    }
                }) {
                    // Success icon
                    Div({
                        style {
                            property("width", "80px")
                            property("height", "80px")
                            property("margin", "0 auto 24px")
                            property("background-color", DesignTokens.Colors.success.toString())
                            property("border-radius", "50%")
                            property("display", "flex")
                            property("align-items", "center")
                            property("justify-content", "center")
                            property("font-size", "40px")
                        }
                    }) {
                        Text("✓")
                    }

                    H3({
                        style {
                            property("font-size", "24px")
                            property("font-weight", "600")
                            property("margin-bottom", "12px")
                            property("color", DesignTokens.Colors.text.toString())
                        }
                    }) {
                        Text("準備完了！")
                    }

                    P({
                        style {
                            property("color", DesignTokens.Colors.textSecondary.toString())
                            property("margin-bottom", "32px")
                            property("line-height", "1.6")
                        }
                    }) {
                        Text("さあ、読書の旅を始めましょう！")
                    }

                    // Action buttons
                    Div({
                        classes(AppStyleSheet.loginButtonGroup)
                    }) {
                        Button({
                            classes(AppStyleSheet.primaryButton)
                            onClick { onSetupTotp() }
                            style {
                                property("margin-bottom", "12px")
                                property("background-color", DesignTokens.Colors.success.toString())
                                property("border-color", DesignTokens.Colors.success.toString())
                            }
                        }) {
                            Text("🔒 2要素認証を設定する（推奨）")
                        }

                        Button({
                            classes(AppStyleSheet.secondaryButton)
                            onClick { onStart() }
                        }) {
                            Text("後で設定する（使い始める）")
                        }
                    }

                    P({
                        style {
                            property("margin-top", "16px")
                            property("font-size", "13px")
                            property("color", DesignTokens.Colors.textSecondary.toString())
                        }
                    }) {
                        Text("※ 2要素認証は後から設定画面で有効にすることもできます")
                    }
                }
            }
        }
    }
}

/**
 * Data class for onboarding step content
 */
data class OnboardingStep(
    val icon: String,
    val title: String,
    val description: String
)
